import { MailCheck, MailWarning, RefreshCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import type { InboxStatus } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ToneChip } from '../../../../components/Tone';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import styles from './MailboxLine.module.css';

/**
 * Inbound mail's mailbox line (Option A), today's facts kept: which mailbox, how often it is read, when it was last
 * read, whether that worked (with the reason when it didn't), and Check now. With no mailbox set up it says so, and how.
 */
export function MailboxLine({ status, canCheck, isChecking, onCheck }: { readonly status: InboxStatus | undefined; readonly canCheck: boolean; readonly isChecking: boolean; readonly onCheck: () => void }): ReactNode {
  if (!status) {
    return (
      <section className={styles.line} aria-label="Mailbox">
        <SkeletonLines count={1} />
      </section>
    );
  }
  if (!status.isConfigured) {
    return (
      <section className={styles.line} aria-label="Mailbox">
        <p className={styles.plain}>No mailbox is set up, so replies are not read yet. Set IMAP_HOST, IMAP_USER and IMAP_PASSWORD for the recruitment mailbox (Hostinger Titan, or any IMAP server).</p>
      </section>
    );
  }
  const hasFailed = status.lastError !== null;
  return (
    <section className={styles.line} aria-label="Mailbox">
      <span className={styles.box}>
        <span className={styles.icon} data-failed={hasFailed || undefined} aria-hidden>
          {hasFailed ? <MailWarning size={18} /> : <MailCheck size={18} />}
        </span>
        <span className={styles.facts}>
          <span className={styles.name}>{status.mailbox}</span>
          <span className={styles.sub}>Read every two minutes</span>
        </span>
      </span>
      <span className={styles.facts}>
        <span className={styles.sub}>Last checked</span>
        <span className={styles.value}>
          {status.lastCheckedAt ? (
            <>
              {formatRelative(status.lastCheckedAt)} <span className={styles.sub}>· {formatDateTime(status.lastCheckedAt)}</span>
            </>
          ) : (
            'Not checked yet'
          )}
        </span>
      </span>
      {status.lastCheckedAt && (
        <span className={styles.facts}>
          <span className={styles.sub}>Last check</span>
          <span className={styles.value}>
            <ToneChip tone={hasFailed ? 'urgent' : 'done'}>{hasFailed ? 'Failed' : 'Worked'}</ToneChip>
          </span>
        </span>
      )}
      {hasFailed && <span className={styles.error}>{status.lastError}</span>}
      <span className={styles.grow} />
      {canCheck && (
        <Button variant="outline" size="sm" icon={RefreshCcw} isPending={isChecking} onClick={onCheck}>
          Check now
        </Button>
      )}
    </section>
  );
}
